/**
 * Archive page at /legacy_roadmap — deliberately NOT linked from the nav bar.
 *
 * Flattens the three surfaces that used to hang off the "?" help menu (all of
 * them gated together by feature_roadmap_hints):
 *   1. the /roadmap timeline,
 *   2. the first-visit intro carousel, and
 *   3. the in-app antd <Tour> walkthrough.
 *
 * The intro modal and the tour have been retired as live features, so their
 * copy only exists here now. The timeline still renders live status from
 * useNodeInfo() via getRoadmapPhases(), same as it always did — the current
 * version of it lives at /map.
 */
import { Typography, Timeline, Tag, Divider, Alert } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons';
import { useNodeInfo } from '../../hooks/use_node_info';
import { getRoadmapPhases } from '../../roadmapData';

const { Title, Paragraph, Text } = Typography;

// Verbatim copy from the retired first-visit intro carousel.
const introSlides = [
    {
        img: '/tour/play_over_the_bridge.png',
        alt: 'Go play outside',
        title: 'Welcome to Vellby',
        text: 'Vellby is an invitation to go play outside — to explore the world outside your usual islands.',
    },
    {
        img: '/tour/ecology_of_island.png',
        alt: 'Ecology of an island',
        title: 'The Ecology within your island',
        text: 'Strong boundaries make for strong identities. Your relationships and your community have developed as ecologies within an island.',
    },
    {
        img: '/tour/connect_islands.png',
        alt: 'Connect islands',
        title: 'but we do need Bridges.',
        text: 'Bridges between different are an invitation to explore beyond your own island. Let new relationships flourish.',
    },
    {
        img: '/tour/requisite_variety.png',
        alt: 'Requisite variety',
        title: 'Requisite Variety',
        text: 'Only variety can absorb variety. The richer your map of the world, the better equipped you are to respond to whatever it throws at you.',
    },
    {
        img: '/tour/levels_of_learning.png',
        alt: 'Levels of learning',
        title: 'Levels of Learning',
        text: "Learning isn't just about collecting facts — it's about changing how you learn. Vellby helps you move between levels: from new knowledge, to new patterns, to new ways of seeing.",
    },
];

const entityTypes = [
    { name: 'Context', desc: 'A bounded space — a project, community, or situation' },
    { name: 'Person', desc: 'An individual with identity, roles, and relationships' },
    { name: 'App', desc: 'A channel, room, page or other app, installed in the entities that use it' },
    { name: 'Relationship', desc: 'A named bond that connects any two entities' },
];

const connectionRules = [
    { type: 'Context', rule: 'Can connect with any entity type' },
    { type: 'Relationship', rule: 'Can connect with any entity except another relationship' },
    { type: 'Person', rule: 'Can only connect with relationship or context entities, or have an app installed' },
    { type: 'App', rule: 'Is installed in any entity except another app' },
];

// The walkthrough's last step pointed at a live DOM node (the nav bar). With
// no pointer left, it survives as prose describing what it used to highlight.
const tourSteps = [
    {
        title: 'Entity Types',
        body: (
            <>
                <Paragraph style={{ marginBottom: 10 }}>
                    Everything in Vellby is built from four entity types — like stem cells, each
                    can be shaped into the other, each carrying its own set of attributes:
                </Paragraph>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                    {entityTypes.map(({ name, desc }) => (
                        <div key={name} style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>{name}</div>
                            <div style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--text-secondary)', marginTop: 2 }}>{desc}</div>
                        </div>
                    ))}
                </div>
            </>
        ),
    },
    {
        title: 'Multiple Descriptions',
        body: (
            <>
                <Paragraph style={{ marginBottom: 10 }}>
                    Every entity can carry <strong>multiple descriptions</strong> — each capturing a
                    different facet. When you connect two entities, you choose which description
                    applies to that specific connection.
                </Paragraph>
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>Example</strong><br />
                    Alex has: <em>"lead engineer"</em>, <em>"open-source contributor"</em>, <em>"team mentor"</em>.<br />
                    In <em>Engineering Team</em> → connects as <em>"lead engineer"</em>.<br />
                    In <em>Community</em> → connects as <em>"open-source contributor"</em>.
                </div>
            </>
        ),
    },
    {
        title: 'Connection Rules',
        body: (
            <>
                <Paragraph style={{ marginBottom: 10 }}>
                    The key difference between entity types is the rules that govern how they can connect:
                </Paragraph>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {connectionRules.map(({ type, rule }) => (
                        <div key={type} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: 'var(--fs-sm)' }}>
                            <span style={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 100 }}>{type}</span>
                            <span style={{ color: 'var(--text-secondary)' }}>{rule}</span>
                        </div>
                    ))}
                </div>
            </>
        ),
    },
    {
        title: 'Navigation',
        body: (
            <Paragraph style={{ marginBottom: 0 }}>
                Browse is your home — it lists the people, contexts and relationships on this
                node. "Me" manages your profile.
            </Paragraph>
        ),
    },
];

function LegacyRoadmapPage() {
    const nodeInfo = useNodeInfo();
    const phases = getRoadmapPhases(nodeInfo);

    return (
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px 64px' }}>
            <Title level={3}>Legacy roadmap</Title>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
                message="Archived"
                description="The intro carousel and the in-app product tour have been retired. Their content is kept here for reference, alongside the roadmap they used to sit next to."
            />

            <Title level={4} style={{ marginTop: 0 }}>Roadmap</Title>
            <Paragraph type="secondary">
                Roughly the order things are rolling out. No dates — just what's live and
                what's next.
            </Paragraph>
            <Timeline
                style={{ marginTop: 24 }}
                items={phases.map(phase => ({
                    color: phase.live ? 'green' : 'gray',
                    dot: phase.live ? <CheckCircleFilled /> : <ClockCircleOutlined />,
                    children: (
                        <div>
                            <Text strong>{phase.title}</Text>{' '}
                            <Tag color={phase.live ? 'success' : 'default'} style={{ marginLeft: 4 }}>
                                {phase.live ? 'Live' : 'Coming'}
                            </Tag>
                            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
                                {phase.description}
                            </div>
                        </div>
                    ),
                }))}
            />

            <Divider />

            <Title level={4}>Intro</Title>
            <Paragraph type="secondary">
                The five slides that used to greet you on your first visit.
            </Paragraph>
            {introSlides.map(slide => (
                <div
                    key={slide.title}
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 20,
                        alignItems: 'center',
                        padding: '20px 0',
                        borderBottom: '1px solid var(--border-color-soft)',
                    }}
                >
                    <img
                        src={slide.img}
                        alt={slide.alt}
                        style={{ flex: '1 1 240px', maxWidth: '100%', borderRadius: 8, display: 'block' }}
                    />
                    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 'var(--fs-h4)', marginBottom: 8 }}>
                            {slide.title}
                        </div>
                        <div style={{ fontSize: 'var(--fs-base)', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                            {slide.text}
                        </div>
                    </div>
                </div>
            ))}

            <Divider />

            <Title level={4}>Product tour</Title>
            <Paragraph type="secondary">
                The six steps of the walkthrough that used to run over the live UI.
            </Paragraph>
            {tourSteps.map((step, index) => (
                <div key={step.title} style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ marginBottom: 8 }}>
                        {index + 1}. {step.title}
                    </Title>
                    {step.body}
                </div>
            ))}
        </div>
    );
}

export default LegacyRoadmapPage;
